import isChromatic from 'chromatic/isChromatic';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DetailedHTMLProps } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const useStayInViewportChromatic = (callback: (ids: string[]) => void) => {
  const idsRef = useRef<string[]>([]);

  const update = useDebouncedCallback(() => {
    if (idsRef.current.length) {
      callback(idsRef.current);
      idsRef.current = [];
    }
  });

  return useCallback(
    (
      id: string,
    ): DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    > => ({
      ref: (node) => {
        if (node) {
          idsRef.current.push(id);
          update();
        }
      },
    }),
    [update],
  );
};

const useStayInViewportReal = (callback: (ids: string[]) => void) => {
  const visibleItemsRef = useRef(new Map<string, Element>());
  const [elementToIdMap] = useState<WeakMap<Element, string>>(
    () => new WeakMap(),
  );

  const observerRef = useRef<IntersectionObserver | null>(null);

  const update = useDebouncedCallback(
    () => {
      const ids = [...visibleItemsRef.current.keys()];
      if (ids.length) callback(ids);

      [...visibleItemsRef.current.values()].forEach((node) => {
        observerRef.current?.unobserve(node);
      });

      visibleItemsRef.current.clear();
    },
    200,
    { maxWait: 800 },
  );

  if (!observerRef.current) {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = elementToIdMap.get(entry.target);
        if (!id) return;
        if (entry.isIntersecting) {
          visibleItemsRef.current.set(id, entry.target);
          update();
        } else {
          visibleItemsRef.current.delete(id);
        }
      });
    });
  }

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return useCallback(
    (
      id: string,
    ): DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    > => ({
      ref: (node) => {
        if (node) {
          elementToIdMap.set(node, id);
          observerRef.current?.observe(node);
        }
      },
    }),
    [elementToIdMap],
  );
};

const useStayInViewport = isChromatic()
  ? useStayInViewportChromatic
  : useStayInViewportReal;

export default useStayInViewport;
